import QuestionMarkRundedIcon from '@mui/icons-material/HelpRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Button,
  FormControl,
  Grid,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';

import {
  AlertSubtype,
  subtypeDescriptions,
  visibleTypeToSubtypeAlertMap,
} from 'common/src/types/enums/AlertSubtype.enum';
import { AlertType } from 'common/src/types/enums/AlertType.enum';
import { EmailFrequency } from 'common/src/types/enums/EmailFrequency';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';

import useAuth from 'portal/src/auth/useAuth';
import { IUserFormValues } from 'portal/src/components/systemAdministration/Form/User/UserForm.fields';

import {
  alertTypes,
  buildInitialPreferences,
  emailFrequencyOptions,
  getPreferenceKey,
  SubtypePreferenceState,
} from './helpers';
import {
  columnContentStyles,
  firstColumnWidth,
  secondColumnWidth,
  subtypeRowStyles,
  thirdColumnWidth,
  typeRowStyles,
} from './styling';

enum EnabledMode {
  None = 'None',
  Custom = 'Custom',
  All = 'All',
}

const tabletTooltipProps = {
  enterTouchDelay: 0,
  leaveTouchDelay: 5000,
};

const featureGatedAlertTypes: Partial<Record<AlertType, ProductFeatures>> = {
  [AlertType.Allocations]: ProductFeatures.allocations,
  [AlertType.BillAutomation]: ProductFeatures.billAutomation,
  [AlertType.Reporting]: ProductFeatures.reporting,
};

const AlertPreferencesForm = ({
  control,
  entityIsLoggedInUser,
}: {
  control: Control<IUserFormValues>;
  entityIsLoggedInUser: boolean;
}) => {
  const { featureChecker } = useAuth();

  const { append, update } = useFieldArray({
    control,
    name: 'alertPreferences',
  });
  const alertPreferences = useWatch({
    control,
    name: 'alertPreferences',
  });

  const visibleAlertTypes = alertTypes.filter((type) => {
    const requiredFeature = featureGatedAlertTypes[type];
    return (
      !requiredFeature || featureChecker.productFeatureEnabled(requiredFeature)
    );
  });

  const preferences = useMemo(
    () => buildInitialPreferences(alertPreferences),
    [alertPreferences],
  );

  const visiblePreferences = visibleAlertTypes.flatMap((type) =>
    visibleTypeToSubtypeAlertMap[type].map(
      (subtype) => preferences[getPreferenceKey(type, subtype)],
    ),
  );

  const syncPreference = (
    type: AlertType,
    subtype: AlertSubtype,
    updatedState: SubtypePreferenceState,
  ) => {
    const preference = {
      type,
      subtype,
      enabled: updatedState.enabled,
      frequency: updatedState.frequency,
    };

    const existingIndex = alertPreferences?.findIndex(
      (preference) =>
        preference.subtype === subtype && preference.type === type,
    );

    if (existingIndex !== -1) {
      update(existingIndex, preference);
    } else {
      append(preference);
    }
  };

  const getTypeEnabledMode = (type: AlertType): EnabledMode => {
    const subtypes = visibleTypeToSubtypeAlertMap[type];
    const enabledCount = subtypes.filter(
      (subtype) => preferences[getPreferenceKey(type, subtype)].enabled,
    ).length;

    if (enabledCount === 0) {
      return EnabledMode.None;
    }
    if (enabledCount === subtypes.length) {
      return EnabledMode.All;
    }
    return EnabledMode.Custom;
  };

  const handleSubtypeEnabledChange = (
    type: AlertType,
    subtype: AlertSubtype,
    enabled: boolean,
  ) => {
    const key = getPreferenceKey(type, subtype);
    const updatedState = {
      ...preferences[key],
      enabled,
      frequency: enabled ? preferences[key].frequency : EmailFrequency.None,
    };

    syncPreference(type, subtype, updatedState);
  };

  const handleTypeEnabledModeChange = (
    type: AlertType,
    mode: EnabledMode | null,
  ) => {
    if (!mode || mode === EnabledMode.Custom) {
      return;
    }

    visibleTypeToSubtypeAlertMap[type].forEach((subtype) => {
      const key = getPreferenceKey(type, subtype);
      const updatedState = {
        ...preferences[key],
        enabled: mode === EnabledMode.All,
        frequency:
          mode === EnabledMode.None
            ? EmailFrequency.None
            : preferences[key].frequency,
      };

      syncPreference(type, subtype, updatedState);
    });
  };

  const handleFrequencyChange = (
    type: AlertType,
    subtype: AlertSubtype,
    value: EmailFrequency,
  ) => {
    const key = getPreferenceKey(type, subtype);
    const updatedState = {
      ...preferences[key],
      // Emailing a subtype's alerts requires the subtype to be enabled
      enabled: value !== EmailFrequency.None || preferences[key].enabled,
      frequency: value,
    };

    syncPreference(type, subtype, updatedState);
  };

  const handleDisableAll = (type: 'all' | 'emails-only') => {
    visibleAlertTypes.forEach((alertType) => {
      visibleTypeToSubtypeAlertMap[alertType].forEach((subtype) => {
        const key = getPreferenceKey(alertType, subtype);
        const updatedState = {
          ...preferences[key],
          frequency: EmailFrequency.None,
        };

        if (type === 'all') {
          updatedState.enabled = false;
        }

        syncPreference(alertType, subtype, updatedState);
      });
    });
  };

  const hasAppEnabledAlerts = visiblePreferences.some((pref) => pref.enabled);

  const hasEmailEnabledFrequency = visiblePreferences.some(
    (pref) => pref.frequency !== EmailFrequency.None,
  );

  const alertCategoriesCount = visiblePreferences.filter(
    (pref) => pref.enabled,
  ).length;

  const dailyEmailDigestCount = visiblePreferences.filter(
    (pref) => pref.frequency === EmailFrequency.Daily,
  ).length;

  const weeklyEmailDigestCount = visiblePreferences.filter(
    (pref) => pref.frequency === EmailFrequency.Weekly,
  ).length;

  return (
    <>
      {alertCategoriesCount > 0 && (
        <Box
          display="flex"
          flexDirection="column"
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.200',
            mb: 2,
            p: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <InfoOutlinedIcon color="info" fontSize="medium" />
            <Typography>
              {entityIsLoggedInUser ? 'You are' : 'This user is'} signed up to
              receive:
            </Typography>
          </Box>
          <List dense sx={{ typography: 'body2', ml: 2 }} disablePadding>
            <ListItem>
              <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
              Alerts from {alertCategoriesCount} alert{' '}
              {alertCategoriesCount > 1 ? 'categories' : 'category'}.
            </ListItem>
            {dailyEmailDigestCount > 0 && (
              <ListItem>
                <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
                <Typography fontSize="inherit">
                  A{' '}
                  <Typography
                    component="span"
                    fontWeight="bold"
                    fontSize="inherit"
                  >
                    daily{' '}
                  </Typography>
                  email digest containing the latest alerts from{' '}
                  {dailyEmailDigestCount} alert{' '}
                  {dailyEmailDigestCount > 1 ? 'categories' : 'category'}.
                </Typography>
              </ListItem>
            )}
            {weeklyEmailDigestCount > 0 && (
              <ListItem>
                <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
                <Typography fontSize="inherit">
                  A{' '}
                  <Typography
                    component="span"
                    fontWeight="bold"
                    fontSize="inherit"
                  >
                    weekly{' '}
                  </Typography>
                  email digest containing the latest non-daily alerts from{' '}
                  {weeklyEmailDigestCount} alert{' '}
                  {weeklyEmailDigestCount > 1 ? 'categories' : 'category'}.
                </Typography>
              </ListItem>
            )}
          </List>
        </Box>
      )}
      <Grid container>
        <Grid size={firstColumnWidth} />
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          size={secondColumnWidth}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={500} fontSize="medium">
              Enabled
            </Typography>
            <Tooltip
              placement="top"
              title="Choose which alert categories you want to track in your alerts table and on your profile badge."
              {...tabletTooltipProps}
            >
              <QuestionMarkRundedIcon fontSize="inherit" />
            </Tooltip>
          </Box>
          <Button
            color="info"
            disabled={!hasAppEnabledAlerts}
            disableElevation
            onClick={() => handleDisableAll('all')}
            size="small"
            variant="contained"
            sx={{
              height: 20,
              width: 70,
              textTransform: 'none',
              fontSize: 12,
            }}
          >
            Unset All
          </Button>
        </Grid>
        <Grid
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          size={thirdColumnWidth}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={500} fontSize="medium">
              In Email Digest
            </Typography>
            <Tooltip
              placement="top"
              title="Select which alert categories to include in your daily or weekly email digest."
              {...tabletTooltipProps}
            >
              <QuestionMarkRundedIcon fontSize="inherit" />
            </Tooltip>
          </Box>
          <Button
            color="info"
            disabled={!hasEmailEnabledFrequency}
            disableElevation
            onClick={() => handleDisableAll('emails-only')}
            size="small"
            variant="contained"
            sx={{
              height: 20,
              width: 125,
              textTransform: 'none',
              fontSize: 12,
            }}
          >
            Unset Emails Only
          </Button>
        </Grid>
      </Grid>

      {visibleAlertTypes.map((type) => {
        const hasMultipleSubtypes =
          visibleTypeToSubtypeAlertMap[type].length > 1;

        return (
          <Fragment key={type}>
            <Grid container sx={typeRowStyles}>
              <Grid size={firstColumnWidth}>
                <Typography fontWeight={500}>{type}</Typography>
              </Grid>
              <Grid size={secondColumnWidth} sx={columnContentStyles}>
                {hasMultipleSubtypes && (
                  <ToggleButtonGroup
                    color="secondary"
                    exclusive
                    onChange={(_, value: EnabledMode | null) =>
                      handleTypeEnabledModeChange(type, value)
                    }
                    size="small"
                    sx={{ '& .MuiToggleButton-root': { py: 0 } }}
                    value={getTypeEnabledMode(type)}
                  >
                    <ToggleButton value="None">None</ToggleButton>
                    <ToggleButton disabled value="Custom">
                      Custom
                    </ToggleButton>
                    <ToggleButton value="All">All</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Grid>
              <Grid size={thirdColumnWidth} sx={columnContentStyles} />
            </Grid>

            {visibleTypeToSubtypeAlertMap[type].map((subtype) => (
              <Grid
                container
                key={getPreferenceKey(type, subtype)}
                sx={subtypeRowStyles}
              >
                <Grid size={firstColumnWidth}>
                  <Box display="flex" alignItems="center" gap={0.5} ml={3}>
                    <Typography fontSize={15}>
                      {subtype.charAt(0) + subtype.slice(1).toLowerCase()}
                    </Typography>
                    <Tooltip
                      placement="right-end"
                      title={subtypeDescriptions[subtype]}
                      {...tabletTooltipProps}
                    >
                      <InfoOutlinedIcon color="info" fontSize="inherit" />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid size={secondColumnWidth} sx={columnContentStyles}>
                  <Switch
                    checked={
                      preferences[getPreferenceKey(type, subtype)].enabled
                    }
                    color="secondary"
                    onChange={(_, checked) =>
                      handleSubtypeEnabledChange(type, subtype, checked)
                    }
                  />
                </Grid>
                <Grid size={thirdColumnWidth} sx={columnContentStyles}>
                  <FormControl
                    size="small"
                    sx={{ width: '100%', maxWidth: 100 }}
                  >
                    <Select
                      disabled={
                        !preferences[getPreferenceKey(type, subtype)].enabled
                      }
                      onChange={(event) =>
                        handleFrequencyChange(
                          type,
                          subtype,
                          event.target.value as EmailFrequency,
                        )
                      }
                      onKeyDown={(event) => {
                        if (
                          preferences[getPreferenceKey(type, subtype)]
                            .enabled &&
                          ['Enter', ' ', 'ArrowDown'].includes(event.key)
                        ) {
                          event.stopPropagation();
                        }
                      }}
                      value={
                        preferences[getPreferenceKey(type, subtype)].frequency
                      }
                      sx={{
                        height: 28,
                        backgroundColor:
                          preferences[getPreferenceKey(type, subtype)]
                            .frequency !== EmailFrequency.None
                            ? 'success.light'
                            : '',
                      }}
                    >
                      {emailFrequencyOptions.map((frequency) => (
                        <MenuItem key={frequency} value={frequency}>
                          {frequency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            ))}
          </Fragment>
        );
      })}
    </>
  );
};

export default AlertPreferencesForm;
